import { useCallback, useEffect, useMemo, useState } from 'react';
import { cashAdvanceFormInitialValues } from '../data/cashAdvanceConstants';
import { cancelLoan, createLoan, fetchLoans } from '../services/loanService';
import { subscribeToPurchaseOrderChanges } from '../services/purchaseOrderRealtimeService';
import { fetchSuppliers } from '../services/supplierService';

function useCashAdvances() {
  const [records, setRecords] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [formValues, setFormValues] = useState(cashAdvanceFormInitialValues);
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [cancelingRecordId, setCancelingRecordId] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [realtimeMessage, setRealtimeMessage] = useState('');

  const supplierOptions = useMemo(
    () =>
      suppliers
        .filter((supplier) => String(supplier.status || '').toLowerCase() === 'active')
        .map((supplier) => ({
          value: String(supplier.id),
          label: supplier.companyName,
        }))
        .sort((firstOption, secondOption) =>
          String(firstOption.label || '').localeCompare(String(secondOption.label || ''), undefined, {
            sensitivity: 'base',
          })
        ),
    [suppliers]
  );

  const loadCashAdvances = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
    }
    setLoadError('');

    try {
      const [cashAdvanceResponse, suppliersResponse] = await Promise.all([
        fetchLoans(),
        fetchSuppliers(),
      ]);

      setRecords(Array.isArray(cashAdvanceResponse) ? cashAdvanceResponse : []);
      setSuppliers(Array.isArray(suppliersResponse) ? suppliersResponse : []);
    } catch (error) {
      setLoadError(error.message);
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadCashAdvances();
  }, [loadCashAdvances]);

  useEffect(() => {
    return subscribeToPurchaseOrderChanges((payload) => {
      if (shouldRefreshCashAdvancesForRealtimePayload(payload)) {
        loadCashAdvances(false).then(() => {
          setRealtimeMessage('Cash Advance refreshed with the latest data.');
        });
      }
    });
  }, [loadCashAdvances]);

  useEffect(() => {
    if (!successMessage && !realtimeMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setSuccessMessage('');
      setRealtimeMessage('');
    }, 3500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [realtimeMessage, successMessage]);

  function handleFormChange(event) {
    const { name, value } = event.target;
    const nextValue = name === 'amount' ? formatAmountInputValue(value) : value;

    if (nextValue === null) {
      return;
    }

    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: nextValue,
    }));
    setFormErrors((currentErrors) => ({
      ...currentErrors,
      [name]: '',
    }));
    setSubmitError('');
    setSuccessMessage('');
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const nextErrors = validateCashAdvanceForm(formValues);

    if (Object.keys(nextErrors).length) {
      setFormErrors(nextErrors);
      return false;
    }

    setIsSaving(true);
    setSubmitError('');

    try {
      const numericAmount = parseFormattedNumber(formValues.amount);
      const createdRecord = await createLoan({
        supplierId: Number(formValues.supplierId),
        caDate: formValues.caDate,
        amount: numericAmount,
        remarks: formValues.remarks.trim(),
      });

      setRecords((currentRecords) => [createdRecord, ...currentRecords]);
      setFormValues(cashAdvanceFormInitialValues);
      setFormErrors({});
      setSuccessMessage('CA record added successfully.');
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  async function handleCancelRecord(record) {
    setCancelingRecordId(record.id);
    setSubmitError('');
    setSuccessMessage('');

    try {
      const cancelledRecord = await cancelLoan(record.id);

      setRecords((currentRecords) =>
        currentRecords.map((currentRecord) =>
          currentRecord.id === cancelledRecord.id ? cancelledRecord : currentRecord
        )
      );
      setSuccessMessage('CA record cancelled successfully.');
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setCancelingRecordId(null);
    }
  }

  function handleReset() {
    setFormValues(cashAdvanceFormInitialValues);
    setFormErrors({});
    setSubmitError('');
  }

  return {
    records,
    supplierOptions,
    formValues,
    formErrors,
    isLoading,
    isSaving,
    cancelingRecordId,
    loadError,
    submitError,
    successMessage,
    realtimeMessage,
    hasSuppliers: supplierOptions.length > 0,
    handleFormChange,
    handleCancelRecord,
    handleSubmit,
    loadCashAdvances,
    handleReset,
  };
}

function shouldRefreshCashAdvancesForRealtimePayload(payload = {}) {
  return [
    'purchase-order-supplier-balancing',
    'supplier-savings-release',
  ].includes(payload.changedEntity);
}

function validateCashAdvanceForm(values) {
  const errors = {};

  if (!values.supplierId) {
    errors.supplierId = 'Supplier is required.';
  }

  if (!values.caDate) {
    errors.caDate = 'CA date is required.';
  }

  const amount = parseFormattedNumber(values.amount);

  if (!Number.isFinite(amount) || amount <= 0) {
    errors.amount = 'CA amount must be greater than zero.';
  }

  return errors;
}

function formatAmountInputValue(value) {
  const normalizedValue = String(value || '').replace(/,/g, '');

  if (!/^\d*(?:\.\d{0,2})?$/.test(normalizedValue)) {
    return null;
  }

  if (!normalizedValue) {
    return '';
  }

  const [wholePart, decimalPart] = normalizedValue.split('.');
  const formattedWholePart = wholePart
    ? Number(wholePart).toLocaleString('en-US')
    : '';

  if (normalizedValue.endsWith('.')) {
    return `${formattedWholePart}.`;
  }

  return decimalPart === undefined
    ? formattedWholePart
    : `${formattedWholePart}.${decimalPart}`;
}

function parseFormattedNumber(value) {
  return Number(String(value || '').replace(/,/g, ''));
}

export default useCashAdvances;
